import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpsertExclusion } from '@/api/domains/excluded-products';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { ExclusionScopePicker } from './ExclusionScopePicker';
import {
  countryItemsFrom,
  methodItemsFrom,
  seedScopeSelection,
  type CountryOption,
  type ExclusionRow,
  type MethodOption,
} from './lib';

type Props = {
  row: ExclusionRow | null;
  onOpenChange: (open: boolean) => void;
  methodOptions: MethodOption[];
  countryOptions: CountryOption[];
};

export function EditExclusionDialog({ row, onOpenChange, methodOptions, countryOptions }: Props) {
  const { t } = useTranslation('excluded-products');
  const upsert = useUpsertExclusion();

  const [selectedMethods, setSelectedMethods] = useState<Set<string>>(new Set());
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());

  const methodItems = methodItemsFrom(methodOptions);
  const countryItems = countryItemsFrom(countryOptions);

  const methodKeys = useMemo(
    () => methodOptions.map((option) => option.methodKey),
    [methodOptions],
  );
  const countryCodes = useMemo(() => countryOptions.map((option) => option.code), [countryOptions]);
  const rowId = row?.productId ?? null;

  // Re-seed selections whenever a different product is opened. A stored empty scope
  // is the "all of them" sentinel, so it seeds as every box ticked - otherwise a
  // blanket exclusion would open looking like it excluded nothing. Optional-chain
  // past the scope arrays too, so a stale cache entry can never crash the dialog.
  useEffect(() => {
    setSelectedMethods(
      seedScopeSelection(
        row?.exclusion?.excluded_methods?.map((method) => method.method_key),
        methodKeys,
      ),
    );
    setSelectedCountries(seedScopeSelection(row?.exclusion?.excluded_countries, countryCodes));
    // Keyed on the product **id**, not the row object: the row is rebuilt on every
    // background refetch of the shared list - which any other product's status flip
    // triggers - and re-seeding then would silently discard scope edits the merchant
    // is in the middle of making. The catalog lists are deps because they load from
    // their own query: seeding against an empty list would leave a blanket exclusion
    // showing nothing ticked, with Save disabled and no way out but reopening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId, methodKeys, countryCodes]);

  // Nothing ticked would be sent as an empty array, which the backend reads as
  // "all of them" - the exact opposite of an empty checklist. Deactivate the
  // exclusion instead of saving a scope that means the reverse of what it shows.
  const isEmptySelection = selectedMethods.size === 0 || selectedCountries.size === 0;

  const handleSave = () => {
    if (!row || isEmptySelection) return;
    upsert.mutate(
      {
        shopify_product_id: row.productId,
        title: row.title,
        is_active: row.exclusion?.is_active ?? true,
        excluded_method_keys: [...selectedMethods],
        excluded_countries: [...selectedCountries],
      },
      {
        onSuccess: () => {
          toast.success(t('saved'));
          onOpenChange(false);
        },
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <Dialog open={row !== null} onOpenChange={onOpenChange}>
      {row ? (
        <DialogContent className="max-w-[661px] gap-0 p-0">
          <DialogHeader className="space-y-1.5 px-6 pt-6 text-left">
            <DialogTitle className="text-lg font-medium leading-none">
              {t('dialog.title', { name: row.title ?? row.productId })}
            </DialogTitle>
            <DialogDescription className="text-sm leading-5">
              {t('dialog.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-[30px] px-6 py-8">
            <ExclusionScopePicker
              label={t('dialog.excludeServices')}
              allLabel={t('dialog.allServices')}
              items={methodItems}
              selected={selectedMethods}
              onSelectionChange={setSelectedMethods}
            />
            <ExclusionScopePicker
              label={t('dialog.excludeMarkets')}
              allLabel={t('dialog.allMarkets')}
              items={countryItems}
              selected={selectedCountries}
              onSelectionChange={setSelectedCountries}
            />
          </div>

          <DialogFooter className="flex-col items-stretch gap-2 px-6 pb-6 sm:flex-row sm:items-center sm:justify-end sm:space-x-0">
            {isEmptySelection ? (
              <p className="mr-auto text-sm leading-5 text-muted-foreground">
                {t('dialog.selectAtLeastOne')}
              </p>
            ) : null}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending}
            >
              {t('dialog.close')}
            </Button>
            <Button onClick={handleSave} disabled={upsert.isPending || isEmptySelection}>
              {t('dialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
